"use client"

import { useRef, useState } from "react"
import { Paperclip, Upload, Image, FileText, Plus } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { toast } from "sonner"
import { KnowledgeBaseModal } from "@/components/modal-dialogs/knowledge-base-modal/knowledge-base-modal"
import type { PromptFile } from "./types"
import type { KnowledgeBaseFile } from "@/app/store/api/knowledgeBaseApi"
import { MAX_FILES } from "./types"

interface AttachPopoverProps {
    isPitchDeck: boolean
    isLoading: boolean
    files: PromptFile[]
    onFilesChange: (files: PromptFile[]) => void
    onLogoChange: (file: File | null) => void
}

export function AttachPopover({
    isPitchDeck,
    isLoading,
    files,
    onFilesChange,
    onLogoChange,
}: AttachPopoverProps) {
    const [attachPopoverOpen, setAttachPopoverOpen] = useState(false)
    const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const logoInputRef = useRef<HTMLInputElement>(null)

    const hasReachedLimit = files.length >= MAX_FILES

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || [])
        const validFiles: File[] = []

        selectedFiles.forEach(file => {
            const type = file.type
            if (type === "application/pdf" ||
                type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                type === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
            ) {
                validFiles.push(file)
            } else {
                toast.error(`Invalid file type: ${file.name}. Only PDF, DOCX, and PPTX are allowed.`)
            }
        })

        if (validFiles.length > 0) {
            if (files.length + validFiles.length > MAX_FILES) {
                toast.error(`You can attach a maximum of ${MAX_FILES} files.`)
                const freeSlots = MAX_FILES - files.length
                if (freeSlots > 0) {
                    onFilesChange([...files, ...validFiles.slice(0, freeSlots)])
                }
            } else {
                onFilesChange([...files, ...validFiles])
            }
        }

        e.target.value = ""
    }

    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"]
        if (!validTypes.includes(file.type)) {
            toast.error(`Invalid file type: ${file.name}. Only PNG, JPG, WEBP, and SVG are allowed for logos.`)
            e.target.value = ""
            return
        }

        onLogoChange(file)
        e.target.value = ""
    }

    const handleKnowledgeBaseImport = (kbFiles: KnowledgeBaseFile[]) => {
        if (kbFiles.length > 0) {
            if (files.length + kbFiles.length > MAX_FILES) {
                toast.error(`You can attach a maximum of ${MAX_FILES} files.`)
                const freeSlots = MAX_FILES - files.length
                if (freeSlots > 0) {
                    onFilesChange([...files, ...kbFiles.slice(0, freeSlots)])
                }
            } else {
                onFilesChange([...files, ...kbFiles])
            }
        }
    }

    const onUploadClick = () => {
        setAttachPopoverOpen(false)
        if (!hasReachedLimit) {
            fileInputRef.current?.click()
        } else {
            toast.error(`Maximum ${MAX_FILES} files reached.`)
        }
    }

    const onKnowledgeBaseClick = () => {
        setAttachPopoverOpen(false)
        if (!hasReachedLimit) {
            setIsKnowledgeBaseModalOpen(true)
        } else {
            toast.error(`Maximum ${MAX_FILES} files reached.`)
        }
    }

    return (
        <>
            <Popover open={attachPopoverOpen} onOpenChange={setAttachPopoverOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        disabled={isLoading}
                        className={cn(
                            "flex-shrink-0 flex items-center justify-center p-2 rounded-full transition-all cursor-pointer group",
                            isLoading
                                ? "text-slate-300 cursor-not-allowed"
                                : "text-slate-500 hover:text-slate-800 hover:bg-gray-200"
                        )}
                        title={isPitchDeck ? "Attach files or logo" : "Attach files"}
                    >
                        <Plus strokeWidth={2.5} className={cn("h-4 w-4 transition-transform", !isLoading && "")} />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-52 p-0.5 bg-white border-slate-200 shadow-lg rounded-xl"
                    sideOffset={8}
                    align="start"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <button
                        type="button"
                        onClick={onUploadClick}
                        className="w-full flex items-center gap-2.5 px-4 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                    >
                        <Upload className="h-4 w-4 text-slate-500" />
                        <div className="text-left">
                            <span className="font-medium">Upload Files</span>
                            <p className="text-[8px] text-slate-400">PDF, DOCX, PPTX (max {MAX_FILES})</p>
                        </div>
                    </button>
                    <button
                        type="button"
                        onClick={onKnowledgeBaseClick}
                        className="w-full flex items-center gap-2.5 px-4 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                    >
                        <FileText className="h-4 w-4 text-slate-500" />
                        <div className="text-left flex flex-col">
                            <span className="font-medium">From Knowledge Base</span>
                            <p className="text-[8px] text-slate-400">PDF, DOCX, PPTX (max {MAX_FILES})</p>
                        </div>
                    </button>
                    {isPitchDeck && (
                        <button
                            type="button"
                            onClick={() => {
                                setAttachPopoverOpen(false)
                                logoInputRef.current?.click()
                            }}
                            className="w-full flex items-center gap-2.5 px-4 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                        >
                            <Image className="h-4 w-4 text-slate-500" />
                            <div className="text-left">
                                <span className="font-medium">Upload Logo</span>
                                <p className="text-[8px] text-slate-400">PNG, JPG, WEBP, SVG</p>
                            </div>
                        </button>
                    )}
                </PopoverContent>
            </Popover>

            {/* Hidden file inputs */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                accept=".pdf,.docx,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                className="hidden"
            />
            {isPitchDeck && (
                <input
                    type="file"
                    ref={logoInputRef}
                    onChange={handleLogoSelect}
                    accept=".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                />
            )}

            <KnowledgeBaseModal
                isOpen={isKnowledgeBaseModalOpen}
                onClose={() => setIsKnowledgeBaseModalOpen(false)}
                onImport={handleKnowledgeBaseImport}
                maxSelections={MAX_FILES - files.length}
            />
        </>
    )
}
